/**
 * Login Form Component - Elegant & Professional Design
 * Handles user authentication with EU/Erasmus+ branding
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Container,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { API_URL } from '../config';

interface LoginFormProps {
  onLoginSuccess: (token: string, user: { email: string; name: string }) => void;
  onSwitchToRegister: () => void;
  compact?: boolean; // For use in dialogs
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, onSwitchToRegister, compact = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLoginSuccess(data.access_token, data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #003399 0%, #0052cc 50%, #003399 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 204, 0, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            borderRadius: 4,
            background: 'white',
            boxShadow: '0 20px 60px rgba(0, 51, 153, 0.3)',
          }}
        >
          {/* EU/Erasmus+ Logo Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
        {/* Logo Tondo */}
        <Box
          component="img"
          src="/logo-tondo.png"
          alt="Erasmus+ Logo"
          sx={{
            width: compact ? 80 : 120,
            height: compact ? 80 : 120,
            mb: compact ? 2 : 3,
            objectFit: 'contain',
          }}
        />

        {/* Erasmus+ Text */}
        <Typography
          variant={compact ? "h5" : "h3"}
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#003399',
            mb: 0.5,
            letterSpacing: '-0.5px',
            fontSize: compact ? '1.5rem' : { xs: '2rem', sm: '2.5rem' },
          }}
        >
          Erasmus+
        </Typography>
        {!compact && (
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: '#666',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            Project Generator
          </Typography>
        )}
      </Box>

      {!compact && <Divider sx={{ mb: 4, borderColor: '#e0e0e0' }} />}

      {/* Welcome Message */}
      <Typography
        variant={compact ? "h6" : "h5"}
        component="h2"
        align="center"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: '#1a1a1a',
        }}
      >
        Sign in to your account
      </Typography>
      {!compact && (
        <Typography
          variant="body2"
          align="center"
          sx={{
            mb: 4,
            color: '#666',
            fontSize: '0.95rem',
          }}
        >
          Continue creating professional applications for Erasmus+ projects
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: compact ? 2 : 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
          }}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#003399' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#003399',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#003399',
                    borderWidth: 2,
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#003399' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#003399',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#003399',
                    borderWidth: 2,
                  },
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 3,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0, 51, 153, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #002d7a 0%, #003399 100%)',
                  boxShadow: '0 6px 16px rgba(0, 51, 153, 0.5)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: '#ccc',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Don't have an account yet?
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={onSwitchToRegister}
              sx={{
                cursor: 'pointer',
                color: '#003399',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign up now
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginForm;
